import { AwaitContainer, Card, CardContent, CardHeader, CardTitle, Combobox, Table, TableColumn } from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IntegrationStatisticsByMethod,
  IntegrationStatisticsError,
  useIntegrationStatistics,
} from '@/entities/integrations';

type Props = {
  code: string;
};

type PeriodKey = 'h24' | 'd7' | 'd30';

const PERIOD_HOURS: Record<PeriodKey, number> = {
  h24: 24,
  d7: 24 * 7,
  d30: 24 * 30,
};

export const StatisticsSection: FC<Props> = ({ code }) => {
  const { t } = useTranslation(['integrations']);
  const [period, setPeriod] = useState<PeriodKey>('h24');

  const params = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getTime() - PERIOD_HOURS[period] * 60 * 60 * 1000);
    return { dateFrom: from.toISOString(), dateTo: now.toISOString() };
  }, [period]);

  const { data, isLoading, isFetching } = useIntegrationStatistics(code, params);

  const periodOptions = useMemo(
    () => [
      { value: 'h24', label: t('integrations:statistics.period.h24') },
      { value: 'd7', label: t('integrations:statistics.period.d7') },
      { value: 'd30', label: t('integrations:statistics.period.d30') },
    ],
    [t],
  );

  const methodColumns: TableColumn<IntegrationStatisticsByMethod>[] = useMemo(
    () => [
      {
        accessorKey: 'method',
        header: () => <span>{t('integrations:statistics.byMethod.method')}</span>,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.method}</span>,
      },
      {
        accessorKey: 'totalRequests',
        header: () => <span>{t('integrations:statistics.byMethod.totalRequests')}</span>,
        size: 120,
        cell: ({ row }) => <span className="text-sm">{row.original.totalRequests}</span>,
      },
      {
        accessorKey: 'successCount',
        header: () => <span>{t('integrations:statistics.byMethod.successCount')}</span>,
        size: 120,
        cell: ({ row }) => <span className="text-sm">{row.original.successCount}</span>,
      },
      {
        accessorKey: 'errorCount',
        header: () => <span>{t('integrations:statistics.byMethod.errorCount')}</span>,
        size: 120,
        cell: ({ row }) => <span className="text-sm">{row.original.errorCount}</span>,
      },
      {
        accessorKey: 'avgDurationMs',
        header: () => <span>{t('integrations:statistics.byMethod.avgDurationMs')}</span>,
        size: 140,
        cell: ({ row }) => <span className="text-sm">{row.original.avgDurationMs}</span>,
      },
    ],
    [t],
  );

  const errorColumns: TableColumn<IntegrationStatisticsError>[] = useMemo(
    () => [
      {
        accessorKey: 'errorMessage',
        header: () => <span>{t('integrations:statistics.errors.errorMessage')}</span>,
        cell: ({ row }) => <span className="text-sm">{row.original.errorMessage || '—'}</span>,
      },
      {
        accessorKey: 'count',
        header: () => <span>{t('integrations:statistics.errors.count')}</span>,
        size: 120,
        cell: ({ row }) => <span className="text-sm">{row.original.count}</span>,
      },
    ],
    [t],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xl">
          <span>{t('integrations:statistics.title')}</span>
          <div className="w-full sm:w-60">
            <Combobox
              value={period}
              onChange={(value) => setPeriod((value as PeriodKey) ?? 'h24')}
              options={periodOptions}
              label={t('integrations:statistics.period.label')}
            />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <AwaitContainer isLoading={isLoading || isFetching}>
          {!data || data.summary.totalRequests === 0 ? (
            <div className="text-muted-foreground">{t('integrations:statistics.empty')}</div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryCard
                  label={t('integrations:statistics.summary.totalRequests')}
                  value={data.summary.totalRequests}
                />
                <SummaryCard
                  label={t('integrations:statistics.summary.successCount')}
                  value={data.summary.successCount}
                />
                <SummaryCard label={t('integrations:statistics.summary.errorCount')} value={data.summary.errorCount} />
                <SummaryCard
                  label={t('integrations:statistics.summary.avgDurationMs')}
                  value={`${data.summary.avgDurationMs} ms`}
                />
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">{t('integrations:statistics.byMethod.title')}</h4>
                <Table
                  data={data.byMethod}
                  columns={methodColumns}
                  getKey={(row) => row.method}
                  styles={{ size: 'sm' }}
                />
              </div>

              {data.errors.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">{t('integrations:statistics.errors.title')}</h4>
                  <Table
                    data={data.errors}
                    columns={errorColumns}
                    getKey={(row) => row.errorMessage}
                    styles={{ size: 'sm' }}
                  />
                </div>
              )}
            </div>
          )}
        </AwaitContainer>
      </CardContent>
    </Card>
  );
};

const SummaryCard: FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1 rounded-lg border p-3">
    <span className="text-xs text-muted-foreground uppercase">{label}</span>
    <span className="text-2xl font-medium">{value}</span>
  </div>
);
